// =============================================================================
// FILE: packages/renderer/src/lib/purchaseRequests.ts
// PURPOSE: Client for the R2 manual-payment / license-release workflow.
//   Public: submit a purchase request. Founder/Admin: review the Approvals
//   Inbox, verify payment, release licenses, and read the audit ledger.
// =============================================================================

const FOUNDER_TOKEN_LS = 'rcd2.founder.token';

export interface PurchaseRequest {
  id: string;
  customer_name: string;
  email: string;
  company?: string | null;
  product_id: string;
  product_name: string;
  product_category?: string | null;
  price?: string | null;
  payment_method: string;
  payment_status: 'awaiting_payment' | 'verified';
  license_status: 'pending' | 'activated' | 'rejected';
  license_id?: string | null;
  activation_history: Array<{ action: string; at: string; by: string; license_id?: string }>;
  created_at: string;
}

export interface R2License {
  id: string; request_id: string; customer_name: string; email: string; company?: string | null;
  product_id: string; product_name: string; product_category?: string | null; price?: string | null;
  status: string; perpetual: boolean; expires_at?: string | null;
  approved_by: string; approved_at: string; activated_at: string; created_at: string;
}

export interface AuditEntry {
  id: string; license_id?: string | null; request_id: string;
  action: string; actor: string; detail?: string; created_at: string;
}

function adminHeaders(adminEmail?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem(FOUNDER_TOKEN_LS);
    if (token) h.Authorization = `Bearer ${token}`;
  } catch { /* */ }
  if (adminEmail) h['X-Admin-Email'] = adminEmail;
  return h;
}

// ── Public ────────────────────────────────────────────────────────────────
export async function submitPurchaseRequest(input: {
  customer_name: string; email: string; company?: string;
  product_id: string; product_name: string; product_category?: string; price?: string;
}): Promise<{ ok: boolean; id: string; notice: string; etransfer_email: string; message: string }> {
  const r = await fetch('/api/purchase/request', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
}

// ── Founder / Admin ─────────────────────────────────────────────────────────
export async function fetchPurchaseRequests(adminEmail?: string): Promise<{ requests: PurchaseRequest[]; counts: Record<string, number>; total: number }> {
  const r = await fetch('/api/admin/purchase/requests', { headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Unauthorized or unavailable');
  return r.json();
}

export async function verifyPayment(id: string, adminEmail?: string): Promise<void> {
  const r = await fetch(`/api/admin/purchase/requests/${id}/verify-payment`, { method: 'POST', headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Verify failed');
}

export async function approveLicense(id: string, opts: { expires_at?: string; note?: string }, adminEmail?: string): Promise<{ ok: boolean; license: R2License }> {
  const r = await fetch(`/api/admin/purchase/requests/${id}/approve`, {
    method: 'POST', headers: adminHeaders(adminEmail), body: JSON.stringify(opts),
  });
  if (!r.ok) throw new Error('Approve failed');
  return r.json();
}

export async function rejectRequest(id: string, adminEmail?: string): Promise<void> {
  const r = await fetch(`/api/admin/purchase/requests/${id}/reject`, { method: 'POST', headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Reject failed');
}

export async function fetchLicenses(adminEmail?: string): Promise<{ licenses: R2License[]; total: number }> {
  const r = await fetch('/api/admin/licenses', { headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Unauthorized or unavailable');
  return r.json();
}

export async function fetchAudit(adminEmail?: string): Promise<{ audit: AuditEntry[]; total: number }> {
  const r = await fetch('/api/admin/licenses/audit', { headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Unauthorized or unavailable');
  return r.json();
}
